import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';
import {
  RiTeamLine, RiUserAddLine, RiCalendarCheckLine, RiTaskLine,
  RiArrowRightLine, RiFileList3Line, RiCheckboxCircleLine,
  RiCake2Line, RiSaveLine, RiCheckLine, RiErrorWarningLine,
} from 'react-icons/ri';

function Avatar({ name, email, size = 'md' }) {
  const initials = (name || email || 'U').split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
  const cls = size === 'sm' ? 'w-8 h-8 text-xs rounded-lg' : 'w-10 h-10 text-sm rounded-xl';
  return (
    <div className={`${cls} flex items-center justify-center text-white font-bold flex-shrink-0`}
      style={{ background: 'linear-gradient(135deg,#0984e3 0%,#d500f9 100%)' }}>
      {initials}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, gradient, shadow, loading, to }) {
  const inner = (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-[0_4px_20px_rgba(15,23,42,0.06)] hover:shadow-[0_8px_30px_rgba(15,23,42,0.1)] transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}
          style={{ boxShadow: `0 6px 16px ${shadow}` }}>
          <Icon size={20} className="text-white" />
        </div>
        {to && <RiArrowRightLine size={14} className="text-[#D1D5DB] group-hover:text-[#0984e3] transition-colors mt-1" />}
      </div>
      {loading
        ? <div className="h-8 w-16 bg-[#F3F4F6] rounded-lg animate-pulse mb-1" />
        : <p className="text-[#1F2937] text-3xl font-bold tracking-tight mb-1">{value ?? '–'}</p>}
      <p className="text-[#6B7280] text-sm font-medium">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [users,   setUsers]   = useState([]);
  const [tasks,   setTasks]   = useState([]);
  const [leaves,  setLeaves]  = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Company anniversary setting ──────────────────────────────────────────
  const [anniversary,        setAnniversary]        = useState('');
  const [anniversaryLoading, setAnniversaryLoading] = useState(true);
  const [anniversarySaving,  setAnniversarySaving]  = useState(false);
  const [anniversaryMsg,     setAnniversaryMsg]     = useState({ type: '', text: '' });

  // GET /api/settings/company-anniversary → { company_anniversary_date: "YYYY-MM-DD" | null }
  const loadAnniversary = () => {
    setAnniversaryLoading(true);
    api.get('/settings/company-anniversary')
      .then((res) => {
        const date = res.data?.company_anniversary_date
          ?? res.data?.date          // fallbacks in case the shape changes
          ?? res.data?.value
          ?? '';
        setAnniversary(date ? String(date).slice(0, 10) : '');
      })
      .catch(() => {})
      .finally(() => setAnniversaryLoading(false));
  };
  useEffect(() => { loadAnniversary(); }, []);

  // PUT /api/settings/company-anniversary  body: { date }
  //   → { message, company_anniversary_date }   (admin only)
  // Empty input → send null so the backend clears the stored date.
  const saveAnniversary = async () => {
    setAnniversarySaving(true);
    setAnniversaryMsg({ type: '', text: '' });
    try {
      const trimmed = (anniversary || '').trim();
      const payload = { date: trimmed || null };
      const res = await api.put('/settings/company-anniversary', payload);
      // Sync local state with whatever the server stored.
      const saved = res.data?.company_anniversary_date;
      setAnniversary(saved ? String(saved).slice(0, 10) : '');
      setAnniversaryMsg({
        type: 'success',
        text: res.data?.message
          || (trimmed ? 'Company anniversary saved.' : 'Company anniversary cleared.'),
      });
      setTimeout(() => setAnniversaryMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setAnniversaryMsg({
        type: 'error',
        text: err.response?.data?.message || 'Failed to save. Please try again.',
      });
    } finally {
      setAnniversarySaving(false);
    }
  };

  useEffect(() => {
    Promise.allSettled([
      api.get('/users'),
      api.get('/tasks'),
      api.get('/leaves'),
      api.get('/daily-updates'),
    ]).then(([u, t, l, d]) => {
      if (u.status === 'fulfilled') {
        const data = u.value.data?.users || u.value.data?.data || u.value.data || [];
        setUsers(Array.isArray(data) ? data : []);
      }
      if (t.status === 'fulfilled') {
        const data = t.value.data?.tasks || t.value.data?.data || t.value.data || [];
        setTasks(Array.isArray(data) ? data : []);
      }
      if (l.status === 'fulfilled') {
        const data = l.value.data?.leaves || l.value.data?.data || l.value.data || [];
        setLeaves(Array.isArray(data) ? data : []);
      }
      if (d.status === 'fulfilled') {
        const data = d.value.data?.updates || d.value.data?.data || d.value.data || [];
        setUpdates(Array.isArray(data) ? data : []);
      }
    }).finally(() => setLoading(false));
  }, []);

  const today = new Date().toDateString();
  const stats = {
    employees:    users.filter((u) => u.role === 'employee').length,
    admins:       users.filter((u) => u.role === 'admin').length,
    pendingLeaves: leaves.filter((l) => l.status === 'pending').length,
    openTasks:    tasks.filter((t) => t.status === 'todo' || t.status === 'in_progress').length,
    todayUpdates: updates.filter((u) => new Date(u.createdAt || u.date).toDateString() === today).length,
    completedTasks: tasks.filter((t) => t.status === 'completed').length,
  };

  const taskBreakdown = [
    { label: 'To Do',       count: tasks.filter((t) => t.status === 'todo').length,        color: '#9CA3AF', bg: '#F3F4F6' },
    { label: 'In Progress', count: tasks.filter((t) => t.status === 'in_progress').length, color: '#0984e3', bg: 'rgba(9,132,227,0.1)' },
    { label: 'Completed',   count: tasks.filter((t) => t.status === 'completed').length,   color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  ];

  const recentLeaves  = leaves.filter((l) => l.status === 'pending').slice(0, 4);
  const recentUpdates = [...updates].sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)).slice(0, 5);
  const recentUsers   = [...users].slice(-6).reverse();

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const LEAVE_TYPE_LABEL = { full_day: 'Full Day', first_half: 'First Half', second_half: 'Second Half' };

  return (
    <div className="p-7 space-y-6 bg-[#F8FAFC]">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-meta mb-1">{greeting}</p>
          <h1 className="heading-md text-[#1F2937]">Welcome back, {user?.name || 'Admin'}</h1>
          <p className="text-[#6B7280] text-sm mt-1">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <Link to="/portal/admin/users" className="btn btn-primary flex items-center gap-2" style={{ padding: '0.6rem 1.2rem', fontSize: '13px' }}>
          <RiUserAddLine size={15} /> Add User
        </Link>
      </div>

      {/* ── Stats row ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Total Employees"  value={stats.employees}    icon={RiTeamLine}           gradient="from-[#0984e3] to-[#00b0ff]" shadow="rgba(9,132,227,0.25)"  loading={loading} to="/portal/admin/users" />
        <StatCard label="Open Tasks"       value={stats.openTasks}    icon={RiTaskLine}           gradient="from-[#10b981] to-[#34d399]" shadow="rgba(16,185,129,0.25)" loading={loading} to="/portal/admin/tasks" />
        <StatCard label="Pending Leaves"   value={stats.pendingLeaves} icon={RiCalendarCheckLine} gradient="from-[#f59e0b] to-[#fbbf24]" shadow="rgba(245,158,11,0.25)" loading={loading} to="/portal/admin/leaves" />
        <StatCard label="Updates Today"    value={stats.todayUpdates} icon={RiFileList3Line}      gradient="from-[#7c3aed] to-[#d500f9]" shadow="rgba(213,0,249,0.25)"  loading={loading} to="/portal/admin/updates" />
      </div>

      {/* ── Company anniversary setting ── */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#ec4899] to-[#d500f9] flex items-center justify-center flex-shrink-0"
               style={{ boxShadow: '0 6px 16px rgba(213,0,249,0.25)' }}>
            <RiCake2Line size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <h2 className="text-[#1F2937] font-bold">Company Anniversary</h2>
            <p className="text-[#9CA3AF] text-xs mt-0.5">
              Set the company's founding date. A reminder email is sent to admins every year on this day.
            </p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label htmlFor="anniversary" className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">
                Anniversary date
              </label>
              <input
                id="anniversary"
                type="date"
                value={anniversary}
                onChange={(e) => setAnniversary(e.target.value)}
                disabled={anniversaryLoading || anniversarySaving}
                className="input-field cursor-pointer text-sm"
              />
            </div>
            <button
              onClick={saveAnniversary}
              disabled={anniversaryLoading || anniversarySaving}
              className="btn btn-primary flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ padding: '0.6rem 1rem', fontSize: '13px' }}
            >
              {anniversarySaving
                ? <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                : <RiSaveLine size={15} />}
              Save
            </button>
          </div>
        </div>
        {anniversaryMsg.text && (
          <div className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
            anniversaryMsg.type === 'success'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-red-50 text-red-600 border border-red-200'
          }`}>
            {anniversaryMsg.type === 'success'
              ? <RiCheckLine size={14} />
              : <RiErrorWarningLine size={14} />}
            {anniversaryMsg.text}
          </div>
        )}
      </div>

      {/* ── Middle row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Team Members — 2/3 width */}
        <div className="xl:col-span-2 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
            <div>
              <h2 className="text-[#1F2937] font-bold">Team Members</h2>
              <p className="text-[#9CA3AF] text-xs mt-0.5">{users.length} total · {stats.employees} employees · {stats.admins} admins</p>
            </div>
            <Link to="/portal/admin/users" className="flex items-center gap-1 text-xs text-[#0984e3] font-semibold hover:underline cursor-pointer">
              Manage <RiArrowRightLine size={13} />
            </Link>
          </div>

          {loading ? (
            <div className="divide-y divide-[#F3F4F6]">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="px-6 py-3.5 flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#F3F4F6] rounded-xl animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-2"><div className="h-3.5 bg-[#F3F4F6] rounded w-1/3 animate-pulse" /><div className="h-3 bg-[#F3F4F6] rounded w-1/2 animate-pulse" /></div>
                  <div className="h-6 w-20 bg-[#F3F4F6] rounded-full animate-pulse" />
                </div>
              ))}
            </div>
          ) : recentUsers.length === 0 ? (
            <p className="text-[#9CA3AF] text-sm text-center py-10">No users yet.</p>
          ) : (
            <div className="divide-y divide-[#F3F4F6]">
              {recentUsers.map((u) => (
                <div key={u._id || u.id} className="px-6 py-3.5 flex items-center gap-4 hover:bg-[#FAFBFC] transition-colors">
                  <Avatar name={u.name} email={u.username || u.email_id} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#1F2937] text-sm font-semibold truncate">{u.name || '—'}</p>
                    <p className="text-[#9CA3AF] text-xs truncate">{u.username || u.email_id || ''}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-xs text-[#6B7280]">
                    {u.position && <span className="truncate max-w-[140px]">{u.position}</span>}
                  </div>
                  <span className={u.role === 'admin' ? 'badge badge-purple' : 'badge'}>{u.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right sidebar — task breakdown + leave summary */}
        <div className="space-y-5">

          {/* Task Breakdown */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#1F2937] font-bold">Task Status</h2>
              <Link to="/portal/admin/tasks" className="text-xs text-[#0984e3] font-semibold hover:underline flex items-center gap-1">
                View all <RiArrowRightLine size={12} />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-[#F3F4F6] rounded-xl animate-pulse" />)}</div>
            ) : tasks.length === 0 ? (
              <p className="text-[#9CA3AF] text-sm text-center py-4">No tasks yet.</p>
            ) : (
              <div className="space-y-3">
                {taskBreakdown.map(({ label, count, color, bg }) => {
                  const pct = tasks.length ? Math.round((count / tasks.length) * 100) : 0;
                  return (
                    <div key={label} className="rounded-xl p-3" style={{ background: bg }}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                          <span className="text-xs font-semibold text-[#374151]">{label}</span>
                        </div>
                        <span className="text-xs font-bold" style={{ color }}>{count}</span>
                      </div>
                      <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-1 px-1">
                  <span className="text-xs text-[#9CA3AF]">Total tasks</span>
                  <span className="text-sm font-bold text-[#1F2937]">{tasks.length}</span>
                </div>
              </div>
            )}
          </div>

          {/* Pending Leaves */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#F3F4F6]">
              <h2 className="text-[#1F2937] font-bold">Pending Leaves</h2>
              <Link to="/portal/admin/leaves" className="text-xs text-[#0984e3] font-semibold hover:underline flex items-center gap-1">
                Review <RiArrowRightLine size={12} />
              </Link>
            </div>
            {loading ? (
              <div className="divide-y divide-[#F3F4F6]">{[...Array(3)].map((_, i) => <div key={i} className="px-5 py-3 space-y-1"><div className="h-3.5 bg-[#F3F4F6] rounded w-2/3 animate-pulse" /><div className="h-3 bg-[#F3F4F6] rounded w-1/2 animate-pulse" /></div>)}</div>
            ) : recentLeaves.length === 0 ? (
              <div className="flex items-center gap-2 px-5 py-5">
                <RiCheckboxCircleLine size={18} className="text-[#10b981]" />
                <p className="text-[#9CA3AF] text-sm">No pending leaves.</p>
              </div>
            ) : (
              <div className="divide-y divide-[#F3F4F6]">
                {recentLeaves.map((l) => {
                  const name = l.user?.name || l.employee?.name || 'Employee';
                  return (
                    <div key={l._id || l.id} className="px-5 py-3 hover:bg-[#FAFBFC] transition-colors">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Avatar name={name} size="sm" />
                        <p className="text-[#1F2937] text-xs font-semibold truncate flex-1">{name}</p>
                      </div>
                      <p className="text-[#9CA3AF] text-xs ml-10">
                        {LEAVE_TYPE_LABEL[l.leave_type] || l.leave_type} · {new Date(l.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Recent Daily Updates ── */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
          <div>
            <h2 className="text-[#1F2937] font-bold">Recent Daily Updates</h2>
            <p className="text-[#9CA3AF] text-xs mt-0.5">{stats.todayUpdates} update{stats.todayUpdates !== 1 ? 's' : ''} submitted today</p>
          </div>
          <Link to="/portal/admin/updates" className="flex items-center gap-1 text-xs text-[#0984e3] font-semibold hover:underline cursor-pointer">
            View all <RiArrowRightLine size={13} />
          </Link>
        </div>

        {loading ? (
          <div className="divide-y divide-[#F3F4F6]">{[...Array(4)].map((_, i) => (
            <div key={i} className="px-6 py-4 flex items-start gap-4">
              <div className="w-9 h-9 bg-[#F3F4F6] rounded-xl flex-shrink-0 animate-pulse" />
              <div className="flex-1 space-y-2"><div className="h-3.5 bg-[#F3F4F6] rounded w-1/4 animate-pulse" /><div className="h-3 bg-[#F3F4F6] rounded w-3/4 animate-pulse" /></div>
            </div>
          ))}</div>
        ) : recentUpdates.length === 0 ? (
          <p className="text-[#9CA3AF] text-sm text-center py-10">No updates yet.</p>
        ) : (
          <div className="divide-y divide-[#F3F4F6]">
            {recentUpdates.map((u, i) => {
              const name  = u.user?.name  || u.employee?.name  || u.userName  || 'Employee';
              const email = u.user?.username || u.employee?.username || u.user?.email_id || u.employee?.email_id || u.userEmail || '';
              const isToday = new Date(u.createdAt || u.date).toDateString() === today;
              return (
                <div key={u._id || u.id || i} className="px-6 py-4 flex items-start gap-4 hover:bg-[#FAFBFC] transition-colors">
                  <Avatar name={name} email={email} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-[#1F2937] text-sm font-semibold">{name}</p>
                      {isToday && <span className="badge text-[#10b981] bg-[rgba(16,185,129,0.08)] border-[rgba(16,185,129,0.2)] text-[10px]">Today</span>}
                    </div>
                    <p className="text-[#6B7280] text-xs leading-relaxed line-clamp-2">{u.content || u.text || '—'}</p>
                  </div>
                  <p className="text-[#9CA3AF] text-xs flex-shrink-0">
                    {new Date(u.createdAt || u.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
